import { type ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingScreen } from '@/shared/components/feedback/LoadingScreen';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  readonly children: ReactNode;
}

/**
 * ProtectedRoute - Route guard for authenticated users only
 *
 * Features:
 * - Redirects unauthenticated users to login
 * - Saves intended destination for post-login redirect
 * - Shows loading screen until the initial session check has finished
 * - Handles auth initialization state
 *
 * @example
 * <ProtectedRoute>
 *   <DashboardPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isInitialized, loading, setRedirectPath } =
    useAuth();
  const location = useLocation();
  const hasSetRedirectPath = useRef(false);

  // Save redirect path in useEffect to avoid setState during render
  useEffect(() => {
    // Uninitialised auth says nothing about the user — `isAuthenticated` is
    // still the rehydrated value. Saving a redirect path here would fire for
    // a user who turns out to be signed in.
    if (!isInitialized) {
      return;
    }

    if (!isAuthenticated && !loading && !hasSetRedirectPath.current) {
      const redirectPath = location.pathname + location.search;
      // Only set if not already on login page
      if (location.pathname !== '/login') {
        setRedirectPath(redirectPath);
        hasSetRedirectPath.current = true;
      }
    }

    // Clear flag when authenticated (user logged in)
    if (isAuthenticated) {
      hasSetRedirectPath.current = false;
    }
  }, [
    isAuthenticated,
    isInitialized,
    loading,
    location.pathname,
    location.search,
    setRedirectPath,
  ]);

  // Wait for the initial session check before trusting `isAuthenticated`: the
  // persist middleware rehydrates it from storage, so a user whose Supabase
  // session has expired would otherwise be admitted on stale state and the
  // app would mount family-scoped queries before `checkAuth` redirects.
  if (!isInitialized || loading) {
    return <LoadingScreen />;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated - render protected content
  return <>{children}</>;
}
